import * as React from "react";
import { useParams } from "react-router-dom";
import UserPayroll from "../index";
import { statusToClass } from '../../../../utils/statusStyles';
import { useEffect, useState } from "react";
import UserProfile from "../..";
import { connect } from "react-redux";
import {
  addUserVariablePay,
  deleteUserVariablePay,
  getCompanyVariablePayTypes,
  getUserVariablePaysPerMonth,
  updateUserVariablePay,
} from "../../../../api/Payroll/variablePay";
import YearMonthPicker from "../../../../components/yearMonthPicker";
import {
  CompanyVariablePayTypeView,
  UserVariablePayInput,
  UserVariablePayStatus,
  UserVariablePayView,
  VariablePaySource,
} from "../../../../models/payroll";
import {
  getCompanyRules,
} from "../../../../api/General/Company";
import { GetCompanyRates } from "../../../../api/General/currency";
import { CompanyCurrencyRate, Currency } from "../../../../models/general";
import { useTranslation } from "react-i18next";
import { formatDate, formatNumber } from "../../../../utils";
import CurrencyDropdown from "../../../../components/currencyDropdown";
import { AddVariablePayDialog, EditVariablePayDialog, DeleteVariablePayDialog } from './UserVariablePayDialogs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../../../components/ui/Table";
import Button from "../../../../components/ui/Button";
import AppIcon from "../../../../components/AppIcon";
import { useToast, ToastRenderer } from "../../../../hooks/useToast";

type Props = Readonly<{
  component?: React.ComponentType<any> | undefined;
  role?: any;
  company?: any;
  userId?: any;
  CompanyCurrencyRate?: any;
}>;

const VariablePay = (props: Props) => {
  const { t } = useTranslation('common');
  const userRole = props.role;
  const { id } = useParams();
  const { toast, showToast, dismissToast } = useToast();

  const [addVariablePayDialogOpen, setAddVariablePayDialogOpen] = useState(false);
  const [editVariablePayDialogOpen, setEditVariablePayDialogOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState((new Date().getMonth() + 1));
  const [from, setFrom] = useState(new Date());
  const [to, setTo] = useState(new Date());
  const [variablePays, setVariablePays] = useState<UserVariablePayView[]>([]);
  const [addInput, setAddInput] = useState<UserVariablePayInput>({ userId: id, effectiveDate: new Date() } as UserVariablePayInput);
  const [editId, setEditId] = useState(0);
  const [editInput, setEditInput] = useState<UserVariablePayInput>({ userId: id, effectiveDate: new Date() } as UserVariablePayInput);
  const [deleteId, setDeleteId] = useState<any>(0);
  const [Loading, setLoading] = useState(false);
  const [errorVisible, setErrorVisible] = useState(false);
  const [CompanyCurrencyRateRates, setCompanyCurrencyRateRates] = useState<CompanyCurrencyRate[]>([]);
  const [companyBaseCurrency, setCompanyBaseCurrency] = useState<Currency | null>();
  const [currency, setCurrency] = useState<any>(0);
  const [companyVariablePayTypes, setCompanyVariablePayTypes] = useState<CompanyVariablePayTypeView[]>([]);

  useEffect(() => {
    fetchData(id, year, month, currency?.foreignCurrencyId ?? props.CompanyCurrencyRate?.id);
  }, [id, year, month, currency?.foreignCurrencyId, props.CompanyCurrencyRate?.id]);

  const fetchData = async (id: any, year: any, month: any, currencyId: any) => {
    setLoading(true);
    try {
      const userId = id ?? "";
      const companyVariablePayTypes = await getCompanyVariablePayTypes();
      setCompanyVariablePayTypes(companyVariablePayTypes.payload);

      const variablePays = await getUserVariablePaysPerMonth(
        userId,
        `${year}-${month.toString().padStart(2, "0")}-01`,
        currencyId
      );
      setVariablePays(variablePays.payload);

      setLoading(false);
      setErrorVisible(false);
      const CompanyCurrencyRateRates = await GetCompanyRates()
      const rates: CompanyCurrencyRate[] = []

      const rules = await getCompanyRules(props.company?.id)
      const baseCurr: CompanyCurrencyRate = {
        id: 0,
        baseCurrency: rules.payload.currency,
        baseCurrencyId: rules.payload.currency.id,
        foreignCurrency: rules.payload.currency,
        foreignCurrencyId: 0,
        company: props.company,
        companyId: props.company?.id,
        rate: 0,
        createdOn: new Date(),
        isArchived: false
      }
      rates.push(baseCurr)
      const rates_: CompanyCurrencyRate[] = CompanyCurrencyRateRates.payload
      rates.push(...rates_)
      setCompanyBaseCurrency(rules.payload.currency);
      setCompanyCurrencyRateRates(rates);
    } catch {
      // Fetch errors handled silently — UI shows stale/empty data
    }
  };

  const handleYearMonthChange = (date: Date) => {
    date.setHours(date.getHours() - date.getTimezoneOffset() / 60);
    setYear(date.getFullYear());
    setMonth((date.getMonth() + 1));
    const fromDate = new Date(date);
    fromDate.setDate(fromDate.getDate() + 6);
    const toDate = new Date(fromDate);
    toDate.setDate(toDate.getDate() + 6);
    setFrom(fromDate);
    setTo(toDate);
  };

  const fromMonthDefault = React.useMemo(() => new Date(new Date().getFullYear() - 8, 0, 1), []);
  const toMonthDefault = React.useMemo(() => new Date(new Date().getFullYear() + 1, 11, 31), []);

  const handleOpenVariablePay = () => setAddVariablePayDialogOpen(true);

  const submitVariablePay = async () => {
    setLoading(true);
    try {
      await addUserVariablePay(addInput);
      setAddVariablePayDialogOpen(false);
      fetchData(id, year, month, props.CompanyCurrencyRate?.id);
      setLoading(false);
      setErrorVisible(false);
    } catch (error: any) {
      setErrorVisible(true);
      fetchData(id, year, month, props.CompanyCurrencyRate?.id);
      setLoading(false);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          if (err.description) showToast(err.description, 'error');
        });
      }
    }
  };

  const handleOpenEdit = () => setEditVariablePayDialogOpen(true);

  const submitEditVariablePay = async () => {
    setLoading(true);
    try {
      await updateUserVariablePay(editInput)
      setEditVariablePayDialogOpen(false);
      fetchData(id, year, month, props.CompanyCurrencyRate?.id);
      setLoading(false);
    } catch (error: any) {
      fetchData(id, year, month, props.CompanyCurrencyRate?.id);
      setLoading(false);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          if (err.description) showToast(err.description, 'error');
        });
      }
    }
  };

  const handleOpenDelete = () => setOpenDelete(true);

  const deleteVariablePay = async (Vid: number) => {
    setLoading(true);
    try {
      await deleteUserVariablePay(Vid);
      setOpenDelete(false);
      fetchData(id, year, month, props.CompanyCurrencyRate?.id);
      setLoading(false);
    } catch (error: any) {
      fetchData(id, year, month, props.CompanyCurrencyRate?.id);
      setLoading(false);
      if (error.errors && error.errors.length > 0) {
        error.errors.forEach((err: any) => {
          if (err.description) showToast(err.description, 'error');
        });
      }
    }
  };

  return (
    <div className="flex flex-col flex-1">
      <UserProfile>
            <UserPayroll>
              <div className="flex flex-col gap-6">
                {/* Toolbar */}
                <div className="flex-between flex-wrap">
                  <YearMonthPicker date={from} onChange={handleYearMonthChange} fromMonth={fromMonthDefault} toMonth={toMonthDefault} isRtl={false} />
                  <CurrencyDropdown
                    options={CompanyCurrencyRateRates}
                    value={currency}
                    onOptionChange={(option: any) => {
                      setCurrency(option);
                    }}
                    label={t('Select Currency') as string}
                  />
                  <div className="flex-grow" />
                  <Button
                    variant="default"
                    iconName="Plus"
                    onClick={handleOpenVariablePay}
                    disabled={userRole !== 'Hr'}
                  >
                    {t('NEW VARIABLE PAY')}
                  </Button>
                </div>

                {/* Table */}
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table aria-label="variable-pay-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('Type')}</TableHead>
                          <TableHead>{t('Amount')}</TableHead>
                          <TableHead>{t('Remarks')}</TableHead>
                          <TableHead>{t('Effective Date')}</TableHead>
                          <TableHead className="text-end">{t('Status')}</TableHead>
                          <TableHead>{t('payrollSource')}</TableHead>
                          <TableHead className="text-end">{t('Actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Loading && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-6">
                              <svg className="animate-spin h-8 w-8 text-primary mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            </TableCell>
                          </TableRow>
                        )}
                        {!Loading && variablePays.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center text-muted-foreground py-4">
                              {t('No records found for selected period')}
                            </TableCell>
                          </TableRow>
                        )}
                        {!Loading && variablePays.map((row: UserVariablePayView) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.companyVariablePayType?.name || ''}</TableCell>
                            <TableCell className="font-mono text-sm">{formatNumber(row.amount)}</TableCell>
                            <TableCell className="text-sm">{row.remarks || '-'}</TableCell>
                            <TableCell className="text-sm">{formatDate(new Date(row.effectiveDate))}</TableCell>
                            <TableCell className="text-end">
                              <div className={statusToClass(row.status)}>{t(row.status)}</div>
                            </TableCell>
                            <TableCell>
                              {row.source === VariablePaySource.Tournament ? (
                                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                  {t('payrollSourceTournament')}
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                  {t('payrollSourceManual')}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-end">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={t('Delete') as string}
                                  onClick={() => {
                                    if (row.status === UserVariablePayStatus.Paid || row.status === UserVariablePayStatus.PendingPayment) {
                                      showToast(t('This variable pay is in a status where it cannot be deleted'), 'error');
                                    } else {
                                      setDeleteId(row.id);
                                      handleOpenDelete();
                                    }
                                  }}
                                >
                                  <AppIcon name="Trash2" size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={t('Edit') as string}
                                  onClick={() => {
                                    setEditId(row.id);
                                    setEditInput({
                                      id: row.id,
                                      userId: id,
                                      companyVariablePayTypeId: row.companyVariablePayTypeId,
                                      amount: row.amount,
                                      effectiveDate: row.effectiveDate,
                                      remarks: row.remarks
                                    });
                                    handleOpenEdit();
                                  }}
                                >
                                  <AppIcon name="Pencil" size={16} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

              </div>

              <ToastRenderer toast={toast} onDismiss={dismissToast} />
            </UserPayroll>
      </UserProfile>

      {/* Extracted Dialog Components */}
      <AddVariablePayDialog
        open={addVariablePayDialogOpen}
        onClose={() => setAddVariablePayDialogOpen(false)}
        onSubmit={submitVariablePay}
        input={addInput}
        setInput={setAddInput}
        variablePayTypes={companyVariablePayTypes}
        errorVisible={errorVisible}
        loading={Loading}
      />
      <EditVariablePayDialog
        open={editVariablePayDialogOpen}
        onClose={() => setEditVariablePayDialogOpen(false)}
        onSubmit={submitEditVariablePay}
        input={editInput}
        setInput={setEditInput}
        variablePayTypes={companyVariablePayTypes}
        loading={Loading}
      />
      <DeleteVariablePayDialog
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        onConfirm={() => deleteVariablePay(deleteId)}
        loading={Loading}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    role: state.user.role,
    company: state.user.company,
    CompanyCurrencyRate: state.user.CompanyCurrencyRate,
  };
};
export default connect(mapStateToProps)(VariablePay) as any;
